import { useMutation, useQuery } from '@apollo/client'
import gql from 'graphql-tag'

import {
  GachaTicketConnectionFragmentDoc,
  PurchaseGachaTicketByPoint,
  PurchaseGachaTicketByPointInBulk,
  PurchaseGachaTicketByPointInBulkMutation,
  PurchaseGachaTicketByPointInBulkMutationVariables,
  PurchaseGachaTicketByPointMutation,
  PurchaseGachaTicketByPointMutationVariables,
  TicketChargeQuery,
  TicketChargeQueryVariables,
  UserGachaTicketConnectionFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { data } = useQuery<TicketChargeQuery, TicketChargeQueryVariables>(
    QUERY,
    { variables: { gachaTicketId: `organization:${config.organizationId}` } }
  )
  const [
    purchaseGachaTicketByPoint,
    { loading: gachaTicketByPointPurchasing },
  ] = useMutation<
    PurchaseGachaTicketByPointMutation,
    PurchaseGachaTicketByPointMutationVariables
  >(PurchaseGachaTicketByPoint)
  const [
    purchaseGachaTicketByPointInBulk,
    { loading: gachaTicketByPointInBulkPurchasing },
  ] = useMutation<
    PurchaseGachaTicketByPointInBulkMutation,
    PurchaseGachaTicketByPointInBulkMutationVariables
  >(PurchaseGachaTicketByPointInBulk)

  return {
    state: {
      gachaTicketByPointPurchasing,
      gachaTicketByPointInBulkPurchasing,
      purchasableGachaTickets: data?.purchasableGachaTickets ?? null,
      ticket: data?.user?.gachaTicket?.available ?? null,
      gachaTicket: data?.gachaTicket ?? null,
    },
    actions: {
      async onPurchaseGachaTicket(count, prepaidPoints) {
        if (count === 1) {
          await purchaseGachaTicketByPoint({
            variables: {
              gachaTicketId: `organization:${config.organizationId}`,
              inputs: {
                count: count,
                point: prepaidPoints ?? 0,
              },
            },
          })
          return
        }
        await purchaseGachaTicketByPointInBulk({
          variables: {
            gachaTicketId: `organization:${config.organizationId}`,
            inputs: { point: prepaidPoints ?? 0 },
          },
        })
      },
    },
  }
})

const QUERY = gql`
  query TicketCharge(
    $inputs: PurchasableGachaTicketInput
    $gachaTicketId: ID!
  ) {
    user {
      id
      gachaTicket(id: $gachaTicketId) {
        ...UserGachaTicketConnection
      }
    }
    purchasableGachaTickets(inputs: $inputs) {
      price
      displayCount
      count
      prepaidPoints
      formattedPrice @client
      formattedPrepaidPoint @client
    }
    gachaTicket(id: $gachaTicketId) {
      ...GachaTicketConnection
    }
  }
  ${UserGachaTicketConnectionFragmentDoc}
  ${GachaTicketConnectionFragmentDoc}
`
